import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import StatsContainer from "@/components/StatsContainer";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";

import {
	dateFormat,
	vasOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getCurrentCategory,
	getCurrentPlayer,
} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const fieldDateFormat = "YYYY-MM-DD";

const requiredFields = ["vasValue"];

function VASPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const isPlayer = userRole === "Player";

	const defaultFiltersData = {
		fromDate: moment()
			.startOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
		toDate: moment()
			.endOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
	};

	const [loading, setLoading] = useState(false);
	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const [vas, setVas] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(
			sessionStorage.getItem("vas_filters" + isPlayer ? "_player" : ""),
		) || defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"vas_filters" + isPlayer ? "_player" : "",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			hidden: true,
			value: moment(filtersData?.fromDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						fromDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			hidden: true,
			value: moment(filtersData?.toDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						toDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		{
			key: "categoryId",
			type: "select",
			options: categories.map((c) => {
				return {
					value: c.id,
					label: c.name,
				};
			}),
			sorted: false,
			label: "Categoria",
			customClassName: "col-12 col-sm-4",
			clearable: true,
			value: filtersData?.categoryId,
			setValue: (value) => {
				setFiltersData({...filtersData, categoryId: value});
			},
		},
	];

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openHandleEdit, setOpenHandleEdit] = useState(false);

	const [value, setValue] = useState();
	const [currentVAS, setCurrentVAS] = useState({});
	const [errors, setErrors] = useState([]);

	const getVAS = async () => {
		setLoading(true);
		try {
			const apiFilters = {
				...filtersData,
				categoryId: filtersData?.categoryId?.value,
				seasonId,
			};
			const response = isPlayer
				? await PlayerAPI.playerListVAS(playerId, apiFilters)
				: await PlayerAPI.listVAS(apiFilters);
			setVas(
				response.map((e) => {
					e.playerCategoryName = getCurrentCategory(categories, e);
					// e.playerName = getCurrentPlayer(players, e);
					return e;
				}),
			);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const deleteVAS = async () => {
		setLoading(true);
		try {
			await PlayerAPI.deleteVAS(playerId, currentVAS?.id);
			getVAS();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const updateVAS = async () => {
		setLoading(true);
		try {
			await PlayerAPI.updateVAS(
				isPlayer ? playerId : currentVAS?.playerId,
				currentVAS?.id,
				{
					...currentVAS,
					vasValue: value?.value,
				},
			);
			getVAS();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleEdit(false);
		}
	};

	useEffect(() => {
		if (categories?.length) {
			getVAS();
		}
	}, [filtersData, categories]);

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => {
				return moment(item.vasDate).format(dateFormat);
			},
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push(
			{
				label: "Categoria",
				key: "playerCategoryId",
				customRender: (item) => (item.category ? item.category.name : "---"),
				small: true,
				align: "start",
			},
			{
				label: "Giocatore",
				key: "playerId",
				customRender: (item) =>
					item.player
						? item.player.firstName + " " + item.player.lastName
						: "---",
				align: "start",
			},
		);
	}

	columns.push({
		label: "VAS",
		key: "vas",
		customRender: (item) => {
			return !item.emptyRow
				? vasOptions.filter((o) => o.value === item.vasValue)?.[0]?.label
				: "Nessun dato inserito";
		},
		align: "start",
	});

	const handleEdit = (item) => {
		setValue({
			value: item.vasValue,
			label: vasOptions.filter((o) => o.value === item.vasValue)?.[0]?.label,
		});
		setCurrentVAS(item);
		setOpenHandleEdit(true);
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			onClick: () => navigate("/measurations/vas/new"),
		},
	];

	const moveActions = [
		{
			variant: "outlined",
			label: isPlayer ? "<< mese" : "<< giorno",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
		{
			variant: "outlined",
			label: isPlayer ? "mese >>" : "giorno >>",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
	];

	const notInsertedTqr = [];
	// players
	// 	.filter((p) => parseInt(p.category.split(" ")?.[1]) > 13)
	// 	.filter(
	// 		(p) =>
	// 			!filtersData.categoryId ||
	// 			(filtersData.categoryId && filtersData.categoryId.name === p.category),
	// 	)
	// 	.forEach((p, k) => {
	// 		let found = false;
	// 		vas.forEach((t) => {
	// 			if (t.playerId === p.id) {
	// 				found = true;
	// 			}
	// 		});

	// 		if (!found) {
	// 			notInsertedTqr.push({
	// 				id: "empty-" + k,
	// 				playerId: p.id,
	// 				vasDate: moment(filtersData.fromDate).format("YYYY-MM-DD"),
	// 				playerName: p.firstName + " " + p.lastName,
	// 				playerCategoryName: p.category,
	// 				emptyRow: true,
	// 			});
	// 		}
	// 	});

	const vasData = isPlayer ? [...vas] : [...vas, ...notInsertedTqr];

	const groupedVAS = {};
	categories
		.filter(
			(c) =>
				c.name !== "Prima Squadra" && parseInt(c.name.split(" ")?.[1]) > 13,
		)
		.forEach((c) => {
			const currentCategory = c.name;
			const vasItems = vasData?.filter((t) => {
				return t.playerCategoryName === currentCategory;
			});
			groupedVAS[currentCategory] = vasItems;
		});

	const listConfiguration = {
		data: isPlayer ? vasData : groupedVAS,
		customClassName: (item) => (item.emptyRow ? "warning" : ""),
		handleEdit,
		handleDelete: (item) => {
			setCurrentVAS(item);
			setOpenHandleConfirm(true);
		},
	};

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				actions={
					<MainActions
						actions={isPlayer ? [...moveActions, ...mainActions] : moveActions}
					/>
				}
			/>
			{true ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							filtersData={filters}
							configurationColumns={columns}
							data={vasData}
							fullWidth
							rowActions
							rowClassName={(item) => (item.emptyRow ? "warning" : "")}
							handleDelete={(item) => {
								setCurrentVAS(item);
								setOpenHandleConfirm(true);
							}}
							handleEdit={handleEdit}
							defaultSortedField={isPlayer ? "vasDate" : "playerCategoryName"}
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="vas"
							breadcrumbs
							grouped={!isPlayer}
							filtersData={filters}
							listConfiguration={listConfiguration}
							groupSorted={false}
							// defaultSortedField="playerCategoryName"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : (
				<StatsContainer type="vas" categories={categories} />
			)}
			{openHandleConfirm && (
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={deleteVAS}
					confirmLabel="Conferma"
					close={() => setOpenHandleConfirm(false)}
				/>
			)}
			{openHandleEdit && (
				<VLFModal
					open={openHandleEdit}
					title="Modifica valore"
					content={
						<div className="row">
							<div className={"col-12"}>
								<VLFSelectNew
									options={vasOptions}
									value={value || ""}
									onChange={(value) => {
										setErrors([]);
										setValue(value);
									}}
									required
									label="VAS"
									sorted={false}
									helperText={
										errors.indexOf("vasValue") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("vasValue") !== -1}
								/>
							</div>
						</div>
					}
					confirm={() => {
						const errors = checkRequiredFields(requiredFields, {
							vasValue: value,
						});
						setErrors(errors);

						if (!errors.length) {
							updateVAS();
						}
					}}
					confirmLabel="Conferma"
					close={() => setOpenHandleEdit(false)}
				/>
			)}
		</Page>
	);
}

export default VASPage;
